/**
 * Pure banned-vocabulary matcher — client-safe, zero I/O.
 *
 * This module performs no filesystem or path work and imports no Node
 * builtins, so it stays reachable from the public `@helix/lib` barrel (see
 * packages/lib/scripts/client-reachable-boundary.spec.mjs, which walks the
 * runtime graph from packages/lib/src/index.ts and fails on any Node builtin
 * edge). Callers supply the term list explicitly — there is no default that
 * reaches out to disk.
 *
 * The single source of truth for the term list itself is
 * scripts/ask-helix-bank/executive-vocabulary.json, loaded ONLY by the
 * Node-only counterpart at packages/lib/src/server/executive-vocabulary-loader.ts
 * (exported through `@helix/lib/server`). Consumers:
 * - Prose scanner (scripts/ask-helix-bank/prose-scan.mjs) — imports the built
 *   `@helix/lib/server` entry for both the term list and this matcher.
 * - Decision-modules structural declaration lint
 *   (apps/api/src/decision-modules/decision-modules-banned-vocabulary.spec.ts).
 *
 * Word-boundary, case-insensitive match; hyphens and spaces are interchangeable.
 */

function escapeForRegex(value: string): string {
  const chars = /[.*+?^${}()|[\]\\]/g;
  return value.replace(chars, '\\$&');
}

/**
 * Returns every term from `terms` that appears in `text` (word-boundary,
 * case-insensitive, space/hyphen-interchangeable). `terms` is required — this
 * function does no I/O and has no default vocabulary of its own.
 */
export function bannedVocabularyMatches(text: string, terms: readonly string[]): string[] {
  const hits: string[] = [];
  for (const term of terms) {
    const pattern = escapeForRegex(term).replace(/ /g, '[ -]');
    if (new RegExp(`\\b${pattern}\\b`, 'i').test(text)) hits.push(term);
  }
  return hits;
}
